// TROBAR ELS BUCLES D'UN DIAGRAMA CAUSAL.
//
// Fora de React i sense cap import, com el motor: així es pot provar sol contra grafs de resposta
// coneguda. Vegeu `frontend/prova-bucles.js`.
//
// ── QUÈ FALLAVA ─────────────────────────────────────────────────────────────────────────────
//
// El que hi havia feia un recorregut en profunditat amb un conjunt `visited` **compartit per tota
// la cerca**, i sortia de seguida que un node ja hi era:
//
//     if (visited.has(nodeId)) return;
//
// Això està bé per saber **si hi ha** algun cicle, i està malament per **enumerar-los tots**: el
// primer camí que passa per un node el crema per a tota la resta de la cerca. Amb dos bucles que
// comparteixin una variable —que és el cas corrent, i el que fa interessant un diagrama— només en
// trobava un. També podia comptar el mateix bucle diverses vegades, un cop per cada rotació.
//
// ── COM ES FA BÉ ────────────────────────────────────────────────────────────────────────────
//
// Enumeració de circuits elementals amb el truc de l'índex mínim: es prova cada node com a inici
// i **només s'estén el camí cap a nodes d'índex més gran**. Així cada cicle es troba exactament
// un cop, des del seu node d'índex més petit, i no cal desduplicar rotacions.
//
// El cost creix molt amb els grafs densos, i per això hi ha dos topalls. Per a un diagrama d'aula
// —desenes de variables— no s'hi arriba mai; si algun dia s'hi arriba, val més ensenyar-ne uns
// quants que penjar el navegador.

const MAX_BUCLES = 200;
const MAX_LLARGADA = 12;

// Reforçador o equilibrador.
//
// La regla és la de la disciplina: es compten els lligams NEGATIUS del cicle. Un nombre parell
// («menys per menys») deixa el senyal com era i el bucle **es reforça a si mateix**; un nombre
// senar el gira i el bucle **estira cap a un nivell**.
export const tipusDeBucle = (polaritats) =>
  polaritats.filter((p) => p === '-').length % 2 === 0 ? 'reinforcing' : 'balancing';

export function detectaBucles(variables, relacions) {
  const ordre = variables.map((v) => v.id);
  const index = new Map(ordre.map((id, i) => [id, i]));

  const sortides = new Map(ordre.map((id) => [id, []]));
  for (const r of relacions) {
    if (sortides.has(r.source) && index.has(r.target)) {
      sortides.get(r.source).push({ target: r.target, polarity: r.polarity, id: r.id });
    }
  }

  const bucles = [];
  for (const inici of ordre) {
    if (bucles.length >= MAX_BUCLES) break;
    const iInici = index.get(inici);
    const alCami = new Set([inici]);

    const camina = (node, cami, polaritats) => {
      if (bucles.length >= MAX_BUCLES || cami.length > MAX_LLARGADA) return;
      for (const aresta of sortides.get(node) || []) {
        if (aresta.target === inici) {
          // Tornem a l'inici: és un cicle, i com que només hem passat per nodes d'índex més gran,
          // aquest és l'únic cop que el trobarem.
          bucles.push({
            id: `loop_${bucles.length + 1}`,
            path: [...cami],
            polarities: [...polaritats, aresta.polarity],
            type: tipusDeBucle([...polaritats, aresta.polarity]),
          });
          continue;
        }
        // Només endavant en l'ordre, i sense repetir nodes del camí.
        if (index.get(aresta.target) <= iInici) continue;
        if (alCami.has(aresta.target)) continue;

        alCami.add(aresta.target);
        camina(aresta.target, [...cami, aresta.target], [...polaritats, aresta.polarity]);
        alCami.delete(aresta.target);
      }
    };

    camina(inici, [inici], []);
  }

  // Els curts primer: en un diagrama gran, els bucles petits són els que s'entenen i els que
  // solen manar.
  return bucles.sort((a, b) => a.path.length - b.path.length)
    .map((b, i) => ({ ...b, id: `loop_${i + 1}` }));
}

export default detectaBucles;
